import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ProductQLCollectionItem, ProductQLCollectionQuery } from '@skylab-angular-shared/business';
import { RootContext, RootContextBase } from '@skylab-angular-shared/core';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { DynamicListDirective, QuickListInsComponent, TextareaValueAccessorDirective, ValidateDirective, IconComponent } from '@skylab-angular-shared/ui';
import { CacheableQuickListDataSourceBase, CompanyParametersQuery, CurrencyItem, quickListFilterMethod } from '@skylab-shared/business';
import { CalculationMethod, type ExchangeRateModel } from '@skylab-shared/dataModel';
import { isString } from '@skylab-shared/utils';
import { ProductQuickListHeaderComponent } from '../../quick-list/product-quick-list-item/product-quick-list-header.component';
import { ProductQuickListItemComponent } from '../../quick-list/product-quick-list-item/product-quick-list-item.component';

@Component({
	selector: 'skl-product-picker',
	template: `
		@if (offerId()) {
			<ins-textarea
				class="display--full-width"
				[multiline]="true"
				[ngModel]="innerValue().name"
				(insInput)="setDocumentItemInsertionText($event)"
				[sklValidate]="validationKey()"
				[placeholder]="placeholder()">
			</ins-textarea>
		} @else {
			<skl-quick-list-ins
				displayText="name"
				[clearButton]="clearButton()"
				[ngModel]="innerValue()"
				[multiline]="true"
				(sklInput)="setDocumentItemInsertionText($event!)"
				(sklSelect)="onProductSelect($event)"
				[validationKey]="validationKey()"
				[clearAfterSelect]="clearAfterSelect()"
				[placeholder]="placeholder()">
				<div slot="header">
					<skl-product-quick-list-header
						[vatPayer]="vatPayer()"
						[calculationMethod]="calculationMethod()">
					</skl-product-quick-list-header>
				</div>
				<ins-option class="option option--multiline" *sklDynamicList="let item of documentItemsGridProductFilter()" [value]="item">
					<skl-product-quick-list-item
						[item]="item"
						[currency]="currency()!"
						[exchangeRate]="exchangeRate()!"
						[calculationMethod]="calculationMethod()"
						(onSwitch)="onProductFavouriteSwitch()"
					/>
				</ins-option>
				@if (footer()) {
					<div
						slot="footer"
						class="select-item select-list__footer"
						(click)="footerClicked.emit(productInsertionQuickListText())"
					>
						<div class="add-button"
							[class]="{'display--none': !(productInsertionQuickListText().length > 0) }"
						>
							<skl-icon
								name="plus-circle"
								class="add-button__icon"
							/>
							<span class="add-button__text">{{ 'scenes.sale-document.form.document-items-grid.insertion-row.add-button' | translate }}</span>
						</div>
					</div>
				}
			</skl-quick-list-ins>
		}
		<div
			class="add-item"
			slot="end"
			[class]="{ 'display--none':  !oneTimeProduct() }">
			<skl-icon
				[sklValidateInner]="{key: 'name.' + (sun() || ''), option: 'hide'}"
				[tooltip]="{
					color: 'primary',
					text: 'scenes.sale-document.form.document-items-grid.columns.name.button.save-as-product.tooltip' | translate
				}"
				class="text--clickable"
				name="add-product-to-cart-index"
				(click)="saveProduct.emit()">
			</skl-icon>
		</div>
	`,
	imports: [
		FormsModule,
		// Components
		QuickListInsComponent,
		ProductQuickListItemComponent,
		ProductQuickListHeaderComponent,
		// Directives
		DynamicListDirective,
		ValidateDirective,
		TextareaValueAccessorDirective,
		// Pipes
		TranslatePipe,
		IconComponent
	],
	styles: `
		:host {
			display:flex;
		}
		.add-item {
			padding-left: 0px;
			display: flex;
			justify-content: center;
			flex-direction: column;
		}
	`,
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ProductPickerComponent {
	private rootContext = inject(RootContext) as unknown as RootContextBase;

	public readonly currency = input<CurrencyItem>();
	public readonly clearAfterSelect = input<boolean>(false);
	public readonly clearButton = input<boolean>(true);
	public readonly exchangeRate = input<ExchangeRateModel | null>();
	public readonly footer = input<Boolean>(false);
	public readonly placeholder = input.required<string>();
	public readonly validationKey = input<string>();
	public readonly productDataSource = input.required<CacheableQuickListDataSourceBase<ProductQLCollectionQuery, unknown>>();

	public readonly productId = input<number>();
	public readonly offerId = input<number>();
	public readonly name = input<string>();
	public readonly sun = input<string>();

	public readonly productInsertionQuickListText = signal('');
	public readonly vatPayer = signal(this.rootContext.getPreloaded(CompanyParametersQuery)?.vatPayer ?? false);
	public readonly calculationMethod = computed(() => this.vatPayer() ? CalculationMethod.Net : CalculationMethod.Gross);
	public readonly oneTimeProduct = computed(() => !this.productId() && !!this.name());

	public readonly innerValue = computed(() => ({name: this.name()}));

	public readonly productSelected = output<ProductQLCollectionItem>();
	public readonly productInputChanged = output<string>();
	public readonly footerClicked = output<string>();
	public readonly saveProduct = output();

	public readonly documentItemsGridProductFilter = computed(() => quickListFilterMethod(this.productDataSource()))

	public onProductFavouriteSwitch() {
		this.productDataSource()?.clearCache();
	}

	public setDocumentItemInsertionText(text: string | Event) {
		const inputValue = isString(text) ? text : (text.target as HTMLTextAreaElement).value
		this.productInsertionQuickListText.set(inputValue);
		this.productInputChanged.emit(inputValue);
	}

	public onProductSelect(value: ProductQLCollectionItem): void {
		this.productSelected.emit(value);
	}
}